import { useCallback } from "react";
import { resetStore, loadInitialStoreProducts } from "./store.slice";
import { StoreProductsParams, StoreProductsData } from "./store.types";
import { useAppDispatch, useAppSelector } from "../redux/redux.hook";
import {
  getStoreProducts,
  getStoreSeller,
  getStoreFullData,
  toggleStoreFollow,
} from "./store.thunk";

const useStore = () => {
  const dispatch = useAppDispatch();
  const {
    seller,
    sections,
    tabs,
    search,
    products,
    pagination,
    status,
    sellerStatus,
    followStatus,
    error,
    sellerError,
    followError,
    currentSlug,
  } = useAppSelector((state) => state.store);

  const fetchStoreSeller = useCallback(
    (slug: string) => {
      dispatch(getStoreSeller(slug));
    },
    [dispatch],
  );

  const fetchStoreFullData = useCallback(
    (slug: string) => {
      dispatch(getStoreFullData(slug));
    },
    [dispatch],
  );

  const fetchStoreProducts = useCallback(
    (slug: string, params?: StoreProductsParams) => {
      dispatch(getStoreProducts({ slug, params }));
    },
    [dispatch],
  );

  const fetchNextPage = useCallback(
    (slug: string, params?: Omit<StoreProductsParams, "page">) => {
      if (!pagination?.next_page_url) return;
      dispatch(
        getStoreProducts({
          slug,
          params: { ...params, page: pagination.current_page + 1 },
        }),
      );
    },
    [dispatch, pagination],
  );

  const toggleFollow = useCallback(
    (id: number) => {
      return dispatch(toggleStoreFollow(id));
    },
    [dispatch],
  );

  const reset = useCallback(() => {
    dispatch(resetStore());
  }, [dispatch]);

  const seedStoreProducts = useCallback(
    (data: StoreProductsData, slug: string) =>
      dispatch(loadInitialStoreProducts({ data, slug })),
    [dispatch],
  );

  return {
    seller,
    sections,
    tabs,
    search,
    products,
    pagination,
    status,
    sellerStatus,
    followStatus,
    error,
    sellerError,
    followError,
    currentSlug,
    isLoading: status === "loading",
    isSuccess: status === "success",
    isError: status === "error",
    isIdle: status === "idle",
    isSellerLoading: sellerStatus === "loading",
    isSellerSuccess: sellerStatus === "success",
    isSellerError: sellerStatus === "error",
    isSellerIdle: sellerStatus === "idle",
    isFollowLoading: followStatus === "loading",
    hasNextPage: !!pagination?.next_page_url,
    fetchStoreSeller,
    fetchStoreFullData,
    fetchStoreProducts,
    fetchNextPage,
    toggleFollow,
    reset,
    seedStoreProducts,
  };
};

export default useStore;
